import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpenCheck, Check, GraduationCap, Handshake, Mic, MonitorPlay, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* ================= Block 1 — Hero ================= */

const PartnersHero = () => (
  <section data-testid="partners-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="mx-auto max-w-[62rem] text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            WFII Partnerships
          </span>
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h1 className="font-serif-editorial mx-auto mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Build the Education Layer for Digital Finance
        </h1>
        <p className="mx-auto mt-8 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none lg:text-xl">
          Digital finance adoption depends on more than technology. WFII works
          with organizations to build{" "}
          <br className="hidden lg:block" aria-hidden="true" />
          trust and practical understanding through education, research, and
          industry dialogue.
        </p>
        <p className="mx-auto mt-4 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none lg:text-xl">
          Partners contribute expertise, support trusted educational
          resources, and take part in initiatives{" "}
          <br className="hidden lg:block" aria-hidden="true" />
          that help finance professionals navigate digital finance with
          confidence.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          <Button
            asChild
            className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
            data-testid="partners-hero-become-partner-button"
          >
            <Link to="/partners/apply">
              Become a Partner
            </Link>
          </Button>
          <Link
            to="/contact"
            className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            data-testid="partners-hero-contact-link"
          >
            Contact Us
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={1.75}
            />
          </Link>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — Partnership opportunities ================= */

const OPPORTUNITIES = [
  {
    n: "01",
    title: "Sponsor a Program",
    body: "Support the development of WFII courses and certifications focused on stablecoins, digital assets, payments, accounting workflows, compliance, and financial infrastructure.",
  },
  {
    n: "02",
    title: "Contribute Speakers",
    body: "Share expertise through webinars, panels, roundtables, interviews, or educational sessions for WFII members and program participants.",
  },
  {
    n: "03",
    title: "Support a Research Guide",
    body: "Contribute to practical guides, checklists, briefings, and educational resources designed for finance professionals and business teams.",
  },
  {
    n: "04",
    title: "Join a Working Group",
    body: "Participate in focused discussions around digital finance education, professional standards, operational readiness, and responsible adoption.",
  },
  {
    n: "05",
    title: "Host a Webinar",
    body: "Collaborate with WFII on educational webinars for CFOs, accountants, bookkeepers, treasury teams, compliance professionals, and business leaders.",
  },
  {
    n: "06",
    title: "Support Scholarships",
    body: "Help expand access to digital finance education for students, early-career professionals, and professionals building new skills.",
  },
];

const OPPORTUNITY_ICONS = {
  "01": GraduationCap,
  "02": Mic,
  "03": BookOpenCheck,
  "04": Users,
  "05": MonitorPlay,
  "06": Award,
};

const Opportunities = () => (
  <section id="partnership-opportunities" data-testid="partners-opportunities-section" className="!mt-[65px] lg:!mt-[120px]">
    <Container className="">
      <div className="grid gap-8 lg:grid-cols-[588px_1fr] lg:gap-10">
        {/* Left — sticky title and photo */}
        <div>
          <div className="lg:sticky lg:top-24">
            <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
              Partnership Opportunities
            </h2>
            <div className="mt-8 overflow-hidden rounded-2xl lg:w-[538px]">
              <img
                src="/partners-opportunities.jpg"
                alt="Panel of three business leaders in discussion on stage"
                className="wfii-photo aspect-[4/3] w-full object-cover"
                loading="lazy"
                data-testid="partners-opportunities-image"
              />
            </div>
          </div>
        </div>

        {/* Right — scrolling cards */}
        <div className="space-y-6">
          {OPPORTUNITIES.map((o) => {
            const Icon = OPPORTUNITY_ICONS[o.n];
            return (
              <div
                key={o.n}
                className="rounded-2xl border border-[hsl(var(--primary))]/12 bg-card p-7 transition-shadow hover:shadow-[0_24px_48px_-28px_hsl(218_51%_15%/0.35)] sm:p-9"
                data-testid={`partners-opportunity-${o.n}`}
              >
                <div className="flex items-center gap-4">
                  <Icon
                    className="h-5 w-5 shrink-0 text-[hsl(var(--wfii-teal))]"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  <h3 className="text-lg font-semibold tracking-[-0.01em] text-foreground sm:text-xl">
                    {o.title}
                  </h3>
                </div>
                <p className="mt-5 max-w-[60ch] text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {o.body}
                </p>
              </div>
            );
          })}
          {/* Founding partner — same card style as the others */}
          <div
            className="rounded-2xl border border-[hsl(var(--primary))]/12 bg-card p-7 transition-shadow hover:shadow-[0_24px_48px_-28px_hsl(218_51%_15%/0.35)] sm:p-9"
            data-testid="partners-founding-card"
          >
            <div className="flex items-center gap-4">
              <Handshake
                className="h-5 w-5 shrink-0 text-[hsl(var(--wfii-teal))]"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <h3 className="text-lg font-semibold tracking-[-0.01em] text-foreground sm:text-xl">
                Become a Founding Partner
              </h3>
            </div>
            <p className="mt-5 max-w-[60ch] text-sm leading-relaxed text-muted-foreground sm:text-base">
              Join WFII as an early partner and help shape the institute’s
              education, research, and community initiatives.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 4 — Who we partner with ================= */

const PARTNER_TYPES = [
  {
    title: "Financial Infrastructure",
    body: "Banks, payment providers, fintech companies, stablecoin platforms, and technology providers.",
  },
  {
    title: "Professional Services",
    body: "Accounting and audit firms, advisory firms, compliance firms, legal professionals, and bookkeeping organizations.",
  },
  {
    title: "Education & Associations",
    body: "Educational institutions, professional associations, training providers, and industry organizations.",
  },
  {
    title: "Research & Industry Contributors",
    body: "Organizations and experts supporting research, working groups, and practical market education.",
  },
];

const WhoWePartnerWith = () => (
  <section data-testid="partners-types-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Who We Partner With
        </h2>
        <p className="mx-auto mt-6 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
          WFII partners with organizations that bring expertise,
          infrastructure, education, or professional perspective to digital{" "}
          <br className="lg:hidden" />
          finance adoption.
        </p>
      </div>
      <div className="mt-[41px] grid gap-6 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4">
        {PARTNER_TYPES.map((t, i) => (
          <div
            key={t.title}
            className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-8 lg:px-[27px]"
            data-testid={`partners-type-card-${i + 1}`}
          >
            <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <h3 className="mt-6 text-lg font-semibold leading-snug tracking-[-0.01em] text-foreground">
              {t.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground lg:text-[16px]">{t.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 5 — Building together ================= */

const TOGETHER = [
  {
    n: "01",
    title: "Shape Professional Education",
    body: "Partners can help define the topics, questions, and practical knowledge that finance professionals need as digital finance becomes part of business operations.",
  },
  {
    n: "02",
    title: "Contribute Trusted Expertise",
    body: "Share industry perspective through speakers, research input, webinars, working groups, and educational resources.",
  },
  {
    n: "03",
    title: "Support Market Readiness",
    body: "Help CFOs, accountants, compliance teams, and business leaders understand new financial infrastructure before they adopt it in real workflows.",
  },
  {
    n: "04",
    title: "Build Credibility Through Education",
    body: (<>Participate in WFII initiatives in a way that supports trust, professional learning,{" "}<br className="hidden lg:block" />and responsible digital finance adoption.</>),
  },
];

const BuildingTogether = () => (
  <section data-testid="partners-together-section" className="!mt-[65px] lg:!mt-[120px]">
    <Container className="">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-center lg:text-5xl">
        Building the Education Layer Together
      </h2>
      <div className="mt-[46px] grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:mt-14">
        {TOGETHER.map((t) => (
          <div
            key={t.n}
            className="group border-t border-[hsl(var(--accent))] pt-6"
            data-testid={`partners-together-${t.n}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium tracking-[0.08em] text-muted-foreground">
                {t.n}
              </span>
              <span
                className="h-2 w-2 rounded-[2px] bg-[hsl(var(--accent))]/0 transition-colors group-hover:bg-[hsl(var(--accent))]/80"
                aria-hidden="true"
              />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">{t.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground lg:text-[16px]">{t.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 6 — Final CTA ================= */

const PartnersFinalCta = () => (
  <section data-testid="partners-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Support Professional Education{" "}
            <br className="hidden lg:block" />
            for Modern Finance
          </h2>
          <div className="mx-auto max-w-2xl lg:max-w-[820px]">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Join WFII in building the knowledge, resources, and industry
              dialogue that help finance{" "}
              <br className="hidden lg:block" />
              professionals navigate digital money, stablecoins, payments, and
              compliance with clarity.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="partners-final-cta-become-partner-button"
              >
                <Link to="/partners/apply">
                  Become a Partner
                </Link>
              </Button>
              <Link
                to="/contact"
                className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-primary-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                data-testid="partners-final-cta-contact-link"
              >
                Contact Us
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={1.75}
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function PartnersPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <PartnersHero />
          <Opportunities />
          <WhoWePartnerWith />
          <BuildingTogether />
          <PartnersFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
